import PropTypes from "prop-types";
import * as React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "../../../themeV2";
import * as constants from "../constants";
import ProductCard from "./ProductCard";
import ShopCard from "./ShopCard";
import css from "./ShopsSlider.module.css";

export default function ShopsSlider({
  products = [],
  influencers = [],
  updateFavouriteStatus,
  look = constants.LOOK_TALL,
  isMobile,
  count = false,
  recommendedShops = false,
}) {
  const trackRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const Component =
    constants.isProduct(look) || products?.length ? ProductCard : ShopCard;

  const data = products?.length ? products : influencers;

  const cardWidth = constants.cardWidthFor({ isMobile, look });
  const imageHeight = constants.imageHeightFor({ isMobile, look });

  const updateScrollButtons = React.useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  React.useEffect(() => {
    updateScrollButtons();
  }, [data.length, updateScrollButtons]);

  React.useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollButtons]);

  const scrollBy = React.useCallback(
    (direction) => {
      const el = trackRef.current;
      if (!el) return;

      const visibleCards = Math.floor(el.clientWidth / (cardWidth + 16));
      const scrollAmount = visibleCards * (cardWidth + 16);
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      el.scrollBy({
        left: direction * scrollAmount,
        behavior: reducedMotion ? "auto" : "smooth",
      });
    },
    [cardWidth],
  );

  return (
    <div className={css.grid}>
      {canScrollLeft && (
        <div
          className={`${css.navBtn} ${css.navBtnLeft}`}
          style={{ "--nav-height": `${imageHeight}px` }}
        >
          <button
            className={css.iconButton}
            onClick={() => scrollBy(-1)}
            aria-label="Scroll left"
          >
            <ChevronLeftIcon />
          </button>
        </div>
      )}

      <div ref={trackRef} className={css.track} onScroll={updateScrollButtons}>
        {data.map((datum, i) => (
          <div className={css.slide} key={datum.route || datum.product_id || i}>
            <Component
              data={datum}
              updateFavouriteStatus={updateFavouriteStatus}
              look={look}
              isMobile={isMobile}
              count={count}
              recommendedShops={recommendedShops}
              style={{
                width: cardWidth,
                position: "relative",
              }}
              isExpanded
            />
          </div>
        ))}
      </div>

      {canScrollRight && (
        <div
          className={`${css.navBtn} ${css.navBtnRight}`}
          style={{ "--nav-height": `${imageHeight}px` }}
        >
          <button
            className={css.iconButton}
            onClick={() => scrollBy(1)}
            aria-label="Scroll right"
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}
    </div>
  );
}

ShopsSlider.propTypes = {
  products: PropTypes.array,
  influencers: PropTypes.array,
  updateFavouriteStatus: PropTypes.func,
  look: PropTypes.oneOf([
    constants.LOOK_TALL,
    constants.LOOK_WIDE,
    constants.LOOK_PRODUCT,
  ]),
  isMobile: PropTypes.bool,
  count: PropTypes.bool,
  recommendedShops: PropTypes.bool,
};
